/**
 * Builder Prompts
 *
 * Book/chapter building prompts for constructing narrative content.
 *
 * @module config/prompts/builder-prompts
 */
// ═══════════════════════════════════════════════════════════════════════════
// BUILDER PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export const BUILDER_OUTLINE_CREATION = {
    id: 'BUILDER_OUTLINE_CREATION',
    name: 'Builder Outline Creation',
    description: 'Generate a chapter outline from passages',
    template: `Create a chapter outline from these passages about "{{theme}}".

Passages:
{{passages}}

Create a logical structure with:
- Introduction that hooks the reader
- 3-5 main sections with clear themes
- Transitions between sections
- Conclusion that provides closure

Respond with JSON:
{
  "title": "Chapter title",
  "introduction": "Opening paragraph summary",
  "sections": [
    { "title": "Section title", "theme": "Core theme", "passageIds": ["id1", "id2"] }
  ],
  "conclusion": "Closing summary"
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.5,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_SECTION_COMPOSITION = {
    id: 'BUILDER_SECTION_COMPOSITION',
    name: 'Builder Section Composition',
    description: 'Compose a section from passages',
    template: `Compose a coherent section for the chapter "{{chapterTitle}}".

Section theme: {{sectionTheme}}
Voice guidance: {{voiceGuidance}}

Source passages:
{{passages}}

Requirements:
- Weave passages naturally into flowing prose
- Maintain the author's voice throughout
- Add minimal bridging text where needed
- Preserve key ideas and insights

Output the composed section text only.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 4096,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_TRANSITION_GENERATION = {
    id: 'BUILDER_TRANSITION_GENERATION',
    name: 'Builder Transition Writing',
    description: 'Generate transitions between sections',
    template: `Write a brief transition (1-2 sentences) that bridges from one section to the next in a chapter about "{{theme}}".

Previous section ended with:
{{previousEnding}}

Transitioning to: "{{nextSectionPreview}}"

The transition should feel natural and maintain flow.

Output only the transition text.`,
    requirements: {
        capabilities: [],
        temperature: 0.7,
        maxTokens: 256,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_STRUCTURE_ANALYSIS = {
    id: 'BUILDER_STRUCTURE_ANALYSIS',
    name: 'Builder Structure Analysis',
    description: 'Analyze chapter narrative structure',
    template: `Analyze this chapter's structure:
1. Identify the narrative arc (building/peak/resolution/flat)
2. Evaluate pacing (0-1 scale)
3. Find structural issues (weak transitions, abrupt shifts, imbalanced sections)
4. Suggest improvements

Chapter:
{{content}}

Respond with JSON:
{
  "narrativeArc": "building|peak|resolution|flat",
  "pacingScore": 0.0-1.0,
  "issues": ["Issue description"],
  "suggestions": ["Improvement suggestion"]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_DRAFT_REVISION = {
    id: 'BUILDER_DRAFT_REVISION',
    name: 'Builder Draft Revision',
    description: 'Revise chapter with specific focus areas',
    template: `Revise this chapter draft. Focus on: {{focusAreas}}

Draft:
{{content}}

Apply targeted improvements while preserving voice and meaning.
Output the revised text.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_IMPROVEMENT_SUGGESTIONS = {
    id: 'BUILDER_IMPROVEMENT_SUGGESTIONS',
    name: 'Builder Improvement Suggestions',
    description: 'Suggest specific chapter improvements',
    template: `Analyze this chapter and suggest specific improvements.
Focus on actionable changes, not general advice.

Chapter:
{{content}}

Respond with JSON:
{
  "suggestions": [
    {
      "location": "Description of where in text",
      "issue": "What's wrong",
      "suggestedFix": "Specific fix"
    }
  ]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.4,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_STYLE_ANALYSIS = {
    id: 'BUILDER_STYLE_ANALYSIS',
    name: 'Builder Style Analysis',
    description: 'Analyze text style against persona',
    template: `Analyze this text's style compared to the persona "{{personaName}}".

Persona traits: {{voiceTraits}}
Persona tone: {{toneMarkers}}
{{#if forbiddenPhrases}}
Forbidden phrases to check: {{forbiddenPhrases}}
{{/if}}

Text:
{{content}}

Evaluate: voice consistency, formality match, pattern usage, forbidden phrase presence.

Respond with JSON:
{
  "voiceMatch": 0.0-1.0,
  "issues": ["Issue description"],
  "suggestions": ["Suggestion"],
  "forbiddenPhrasesFound": ["phrase found"]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_ISSUE_FIX = {
    id: 'BUILDER_ISSUE_FIX',
    name: 'Builder Issue Fix Application',
    description: 'Fix specific issue in text',
    template: `You need to fix an issue in this text.

Issue: {{issueDescription}}
Location: {{location}}
Suggested fix: {{suggestedFix}}

Text:
{{content}}

Apply the fix naturally. Output only the corrected text.`,
    requirements: {
        capabilities: [],
        temperature: 0.5,
        maxTokens: 4096,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_FOCUSED_REVISION = {
    id: 'BUILDER_FOCUSED_REVISION',
    name: 'Builder Focused Revision',
    description: 'Revision based on Reviewer feedback',
    template: `Revise this chapter text. Focus specifically on:
{{#each focusAreas}}
- {{this}}
{{/each}}

Issues to address:
{{#each issues}}
- {{this.type}}: {{this.description}} at {{this.location}}
{{/each}}

Text:
{{content}}

Output the revised text with all issues addressed.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['builder'],
};
// ═══════════════════════════════════════════════════════════════════════════
// INLINE MIGRATION PROMPTS (formerly hardcoded in builder)
// ═══════════════════════════════════════════════════════════════════════════
export const BUILDER_INLINE_OUTLINE = {
    id: 'BUILDER_INLINE_OUTLINE',
    name: 'Builder Inline Outline Creation',
    description: 'Chapter outline creation (migrated from builder/index.ts inline)',
    template: `You are structuring a book chapter about "{{theme}}".
Given these passages with their roles (anchor=main point, supporting=evidence, contrast=counterpoint, evidence=data),
create an optimal chapter outline.

Target length: ~{{targetLength}} words

Respond with JSON: {
  sections: [
    { type: "opening"|"body"|"transition"|"conclusion", passageIds: [], purpose: "brief description" }
  ],
  suggestedTitle: "chapter title"
}

Ensure logical flow and narrative arc.`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.5,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_INLINE_COMPOSE = {
    id: 'BUILDER_INLINE_COMPOSE',
    name: 'Builder Inline Section Compose',
    description: 'Section composition system prompt (migrated from builder/index.ts inline)',
    template: `You are a skilled writer composing a book chapter.
{{#if styleGuidelines}}Style guidelines: {{styleGuidelines}}{{/if}}
{{#if personaRef}}Writing as: {{personaRef}}{{/if}}
{{#if isFirst}}This is the chapter opening - make it compelling.{{/if}}
{{#if isLast}}This is the chapter ending - provide closure.{{/if}}

Integrate the source passages naturally. Preserve their core ideas but improve flow.
Write in a consistent voice. Output only the composed text, no meta-commentary.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 4096,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_INLINE_STRUCTURE_ANALYSIS = {
    id: 'BUILDER_INLINE_STRUCTURE_ANALYSIS',
    name: 'Builder Inline Structure Analysis',
    description: 'Chapter structure analysis (migrated from builder/index.ts inline)',
    template: `Analyze this chapter's structure:
1. Identify the narrative arc (building/peak/resolution/flat)
2. Evaluate pacing (0-1 scale)
3. Find structural issues (weak transitions, abrupt shifts, imbalanced sections)
4. Suggest improvements

Respond with JSON: {
  narrativeArc: "building"|"peak"|"resolution"|"flat",
  pacingScore: 0-1,
  issues: [],
  suggestions: []
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_INLINE_REVISION = {
    id: 'BUILDER_INLINE_REVISION',
    name: 'Builder Inline Draft Revision',
    description: 'Draft revision system prompt (migrated from builder/index.ts inline)',
    template: `Revise this chapter draft. Focus on: {{focusPrompt}}
Preserve the core ideas and passage content. Improve the specified areas.
Output the revised chapter text only.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_INLINE_SUGGESTIONS = {
    id: 'BUILDER_INLINE_SUGGESTIONS',
    name: 'Builder Inline Improvement Suggestions',
    description: 'Improvement suggestions (migrated from builder/index.ts inline)',
    template: `Analyze this chapter and suggest specific improvements.
For each suggestion, provide:
- Type: structure, voice, pacing, clarity, content
- Location: approximate position in chapter
- Current issue
- Suggested fix

Respond with JSON: { suggestions: [{ type, location, issue, fix }] }`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.4,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['builder'],
};
export const BUILDER_PROMPTS = [
    BUILDER_OUTLINE_CREATION,
    BUILDER_SECTION_COMPOSITION,
    BUILDER_TRANSITION_GENERATION,
    BUILDER_STRUCTURE_ANALYSIS,
    BUILDER_DRAFT_REVISION,
    BUILDER_IMPROVEMENT_SUGGESTIONS,
    BUILDER_STYLE_ANALYSIS,
    BUILDER_ISSUE_FIX,
    BUILDER_FOCUSED_REVISION,
    // Inline migrations
    BUILDER_INLINE_OUTLINE,
    BUILDER_INLINE_COMPOSE,
    BUILDER_INLINE_STRUCTURE_ANALYSIS,
    BUILDER_INLINE_REVISION,
    BUILDER_INLINE_SUGGESTIONS,
];
//# sourceMappingURL=builder-prompts.js.map